import { type Dispatch, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Info from '@/assets/icons/info.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { Card } from '@/components/atoms/Card';
import Checkbox from '@/components/atoms/Checkbox';
import { Overline } from '@/components/atoms/Eyebrow';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import EmptyState from '@/components/molecules/EmptyState';
import { AllocationSummary } from '@/components/organisms/FishGroupForms';
import type { FishGroupPopulationInfo, OverviewCage } from '@/domain/fishGroups';

import { computeAllocationSummary, type WizardValidationError } from '../../operateWizardHelpers';
import type {
    OperateWizardAction,
    WizardDestination,
    WizardSource,
} from '../../operateWizardReducer';
import { ValidationBanner } from '../shared/ValidationBanner';

import { RemainderCard } from './RemainderCard';

import styles from '../../operateModals.module.scss';

interface Props {
    initialSource: WizardSource;
    locationCages: OverviewCage[];
    /**
     * The remainder destination — `null` if the operator toggled it off and
     * wants to vacate the source cage entirely. Located by matching the
     * source's current cage id (same convention as the rest of the wizard).
     */
    remainder: WizardDestination | null;
    errors: WizardValidationError[];
    sources: WizardSource[];
    destinations: WizardDestination[];
    populationById: Map<number, FishGroupPopulationInfo>;
    dispatch: Dispatch<OperateWizardAction>;
}

/**
 * Split mode — Step 1 (Source continuation).
 *
 * Mirrors the canonical 2-step shape used by Split-and-Merge so the two
 * composition-changing flows feel structurally identical:
 *
 *   continuation → target(s) → confirm
 *
 * The operator decides here what — if anything — stays in the source cage:
 *
 *  - **Toggle ON (default)**: a new fish group is created in the source's
 *    own cage holding the remainder. The card below collects its name,
 *    optional count and notes. Same auto-add-on-mount behaviour as the
 *    Split-and-Merge continuation.
 *  - **Toggle OFF**: the operator vacates the source cage entirely. The
 *    full population will land on the target cages picked in Step 2.
 *
 * Either way Step 2 expects at least two destinations total once the
 * operator advances (continuation + ≥1 target, or ≥2 targets) — otherwise
 * the backend's `inferOperationType` would classify the operation as a
 * degenerate merge.
 */
export default function SplitContinuationStep(props: Props) {
    const {
        initialSource,
        locationCages,
        remainder,
        errors,
        sources,
        destinations,
        populationById,
        dispatch,
    } = props;
    const { t } = useTranslation('fishGroups');

    const sourceCageId = initialSource.currentCageId;
    const sourceCageName =
        sourceCageId != null
            ? (locationCages.find((c) => c.cageId === sourceCageId)?.cageName ?? '')
            : '';

    // Auto-add the remainder on first mount so the operator lands on the
    // common "keep some, split the rest" configuration. Idempotent: noop
    // if a destination already targets the source cage.
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally mount-only
    useEffect(() => {
        if (sourceCageId == null) return;
        dispatch({ type: 'addSplitRemainder' });
    }, []);

    const allocationSummary = computeAllocationSummary({
        sources,
        destinations,
        populationById,
    });

    const onToggleRemainder = () => {
        if (remainder) {
            dispatch({ type: 'removeDestination', tempId: remainder.tempId });
        } else {
            dispatch({ type: 'addSplitRemainder' });
        }
    };

    // The Step 2 validator looks at every destination. In this step the
    // operator can only act on the remainder, so we filter errors down to
    // those pertaining to it (or step-agnostic ones with no tempId tag).
    const continuationErrors = errors.filter((err) => {
        if (!('destinationTempId' in err)) return true;
        return remainder != null && err.destinationTempId === remainder.tempId;
    });

    return (
        <section className={styles.body}>
            <div>
                <h4 className={styles.heading}>
                    {t('actions.operate.split.continuation.heading')}
                </h4>
                <p className={styles.description}>
                    {t('actions.operate.split.continuation.description', {
                        cageName: sourceCageName,
                    })}
                </p>
            </div>

            <Card radius="sm" variant="subtle" padding="none" className={styles.contextCard}>
                <Overline>{t('actions.operate.split.sourceLabel')}</Overline>
                <span className={styles.contextValue}>
                    {initialSource.name}
                    {sourceCageName && (
                        <span className={styles.summaryMeta}> ({sourceCageName})</span>
                    )}
                </span>
            </Card>

            <AllocationSummary
                summary={allocationSummary}
                sources={sources}
                populationById={populationById}
            />

            {continuationErrors.length > 0 && <ValidationBanner errors={continuationErrors} />}

            {sourceCageId != null ? (
                <div className={styles.remainderSection}>
                    <label className={styles.toggleRow}>
                        <Checkbox
                            checked={remainder !== null}
                            onCheckedChange={onToggleRemainder}
                        />
                        <span className={styles.toggleLabel}>
                            {t('actions.operate.split.keepRemainderToggle', {
                                cageName: sourceCageName,
                            })}
                        </span>
                    </label>
                    <p className={styles.inputHint}>
                        {t('actions.operate.split.keepRemainderHint')}
                    </p>

                    {remainder ? (
                        <RemainderCard
                            destination={remainder}
                            sourceCageName={sourceCageName}
                            sourceName={initialSource.name}
                            dispatch={dispatch}
                        />
                    ) : (
                        <Alert
                            tone="info"
                            size="small"
                            icon={<SvgIcon icon={Info} size="small" color="currentColor" />}>
                            {t('actions.operate.split.continuation.vacateHint', {
                                cageName: sourceCageName,
                            })}
                        </Alert>
                    )}
                </div>
            ) : (
                <EmptyState
                    title={t('actions.operate.split.continuation.unavailable')}
                    appearance="quiet"
                />
            )}
        </section>
    );
}
